import { InitiatePaymentMutationVariables, PortalWorkoutPlanFragment } from '@willow/graphql-iso/src/portal';
import { add } from '@willow/shared-iso';
import { EscrowPaymentHistoryItemId } from '@willow/types-iso';

import { TPaymentFormShape } from './form';
import { AdditionalPayment } from './PaymentForm';

/**
 * On the frontend, we may not necessarily be showing the actual amount due on the loan in the case of an active workout plan.
 * IE a loan is past due $1000, but with the active flex mod plan, the borrower is only required to pay $500
 *
 * However, payment allocations should be based on the actual amount due on the loan
 * Example: paying $600 on that flex mod plan should not be a $100 overpayment
 */
export const getPaymentInput = ({
  paymentIntention,
  paymentAmount,
  escrowShortageAnalysisId,
  activeWorkoutPlan,
  totalAmountDue,
  totalAmountDueToReinstateLoan,
  additionalEscrowShortage,
  additionalPayment,
}: {
  paymentIntention: TPaymentFormShape['paymentIntention'];
  paymentAmount: number;
  escrowShortageAnalysisId: EscrowPaymentHistoryItemId | undefined;
  activeWorkoutPlan: PortalWorkoutPlanFragment | undefined;
  totalAmountDue: number;
  totalAmountDueToReinstateLoan: number;
  additionalEscrowShortage: number;
  additionalPayment: AdditionalPayment | undefined;
}): Pick<
  InitiatePaymentMutationVariables,
  'amount' | 'additionalPrincipal' | 'additionalEscrow' | 'additionalSuspense'
> => {
  const amounts = {
    amount: paymentAmount,
    additionalPrincipal: additionalPayment?.principal ?? 0,
    additionalEscrow: additionalPayment?.escrow ?? 0,
    additionalSuspense: additionalPayment?.suspense ?? 0,
  };

  if (paymentIntention === 'totalWithShortage' && escrowShortageAnalysisId) {
    return {
      ...amounts,
      additionalEscrow: additionalEscrowShortage,
    };
  }

  const amountDue = activeWorkoutPlan ? totalAmountDueToReinstateLoan : totalAmountDue;

  // Underpayment or exact amount due
  if (paymentAmount <= amountDue) {
    return {
      amount: paymentAmount,
      additionalPrincipal: 0,
      additionalEscrow: 0,
      additionalSuspense: 0,
    };
  }

  // Overpayment
  if (additionalPayment) {
    const overpaymentAmount = add(
      amountDue,
      additionalPayment.principal,
      additionalPayment.escrow,
      additionalPayment.suspense,
    );

    return {
      amount: overpaymentAmount,
      additionalPrincipal: additionalPayment.principal,
      additionalEscrow: additionalPayment.escrow,
      additionalSuspense: additionalPayment.suspense,
    };
  }

  // Default case (shouldn't happen, but for safety)
  return amounts;
};
